import type { AxiosError } from 'axios';
import axios from 'axios';
import dayjs from 'dayjs';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { AppCode } from '../../../enum';
import { APIStatus } from '../../../enum';
import type { BaseDashboardParams, Dow, MissedChat, OtherSubject, SubjectTotal } from '../../../models/dashboard';
import type { LineChartData } from '../../../models/echarts';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { type FilteredInfo, type RequestParams, SortedInfo, type TableChange } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import type { UnlockCodeDetails } from '../admin-unlock-codes/adminUnlockCodesSlice';
import type { Organization } from '../orgs/orgsSlice';
import { dateTimeFilterFormat } from '../program-dashboard/program-dashboard.helper';
import type { ChatActivity, DashboardBody, DashboardData } from '../program-dashboard/programDashboardSlice';
import { DASHBOARD_MODAL } from '../program-dashboard/programDashboardSlice';

const { logError } = RaygunErrorHandlerService();

type BQDashboardBody = DashboardBody & {
  unlock_codes: string[];
};

type ModalTypes = MissedChat | OtherSubject;

export type EngagementData = {
  active_guests: number;
  code_validation_dow: Dow[];
  total_guests_finishing_trace_and_ask: number;
  total_guests_chatting: number;
  total_guests_unlocking: number;
  total_guests_reporting_feeling: number;
};

export type BQDashboardParams = BaseDashboardParams & {
  unlock_codes: string[];
};

type BQProgramDashboardSliceType = {
  allUnlockCodes: UnlockCodeDetails[];
  dashboardParams: BQDashboardParams;
  dashboardData: DashboardData;
  guest_engagement: EngagementData;
  feelings: SubjectTotal[];
  risk_signals: DomainScore[];
  chatActivity: ChatActivity;
  csv_exports: {
    trace_and_ask_csv: string;
    feedback_csv: string;
    feelings_csv: string;
    peers_hours_csv: string;
  };
  requestModalParam: RequestParams<ModalTypes>;
  dashboardApiStatus: APIStatus;
  chatActivityApiStatus: APIStatus;
  feelingsApiStatus: APIStatus;
  engagementApiStatus: APIStatus;
  riskSignalsApiStatus: APIStatus;
  traceAndAskApiStatus: APIStatus;
  modal: DASHBOARD_MODAL;
  filtersInfo: string;
};

export type DomainScore = {
  domain: string;
  threshold: number;
  percent_over_threshold: number;
  guest_at_risk_count: number;
  guest_count: number;
};

const initialState: BQProgramDashboardSliceType = {
  allUnlockCodes: [],
  dashboardParams: {
    app_code: AppCode.BuddysQuest,
    start_date: dayjs().subtract(1, 'month'),
    end_date: dayjs(),
    selected_org_code: 'allOrgs',
    unlock_codes: [],
  },
  requestModalParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<ModalTypes>('total', 'descend'),
    filteredInfo: {},
  },
  dashboardData: {
    total_chats: null,
    average_chat_length: null,
    chat_lengths: [],
    unique_guests_chatting: null,
    percentage_repeat_chatters: null,
    issues: {
      reports_of_abuse: null,
      reports_of_emergency: null,
      blocked_guests: null,
      missed_chat_count: null,
    },
    feedback: {
      chat_helpful: [],
      chat_end_reasons: [],
      chat_subjects: [],
      other_chat_subjects: [],
      chat_referred_guest: [],
      guest_feelings: [],
      guest_feelings_reporting_rate: [],
    },
    advocate_dow: [],
  },
  chatActivity: {
    data: [],
    csv: '',
    filename: '',
  },
  guest_engagement: {
    active_guests: 0,
    code_validation_dow: [],
    total_guests_finishing_trace_and_ask: 0,
    total_guests_chatting: 0,
    total_guests_unlocking: 0,
    total_guests_reporting_feeling: 0,
  },
  csv_exports: {
    trace_and_ask_csv: '',
    feedback_csv: '',
    feelings_csv: '',
    peers_hours_csv: '',
  },
  feelings: [],
  risk_signals: [],
  modal: DASHBOARD_MODAL.IDLE,
  dashboardApiStatus: APIStatus.IDLE,
  chatActivityApiStatus: APIStatus.IDLE,
  feelingsApiStatus: APIStatus.IDLE,
  engagementApiStatus: APIStatus.IDLE,
  riskSignalsApiStatus: APIStatus.IDLE,
  traceAndAskApiStatus: APIStatus.IDLE,
  filtersInfo: '',
};

const getBQFiltersInfo = (body: BQDashboardBody, orgs: Organization[]) => {
  let text = `${dayjs(body.start_date).format('YYYY-MM-DD')} - ${dayjs(body.end_date).format('YYYY-MM-DD')}`;
  const selectedOrgName = orgs.find((o) => o.code === body.selected_org_code)?.name;
  if (selectedOrgName) {
    text += ` ${selectedOrgName}`;
  }
  if (body.unlock_codes && body.unlock_codes.length > 0) {
    text += ` (${body.unlock_codes.join(', ')})`;
  }
  return text;
};

// oxlint-disable-next-line typescript/no-invalid-void-type
export const loadDashboard = createAsyncThunk<void, undefined, AsyncThunkConfig>(
  'bqProgramDashboard/loadDashboard',
  async (_, thunkAPI) => {
    try {
      const promises: { abort: VoidFunction }[] = [];
      thunkAPI.signal.addEventListener('abort', () => {
        promises.forEach((p) => p.abort());
      });
      const dashboardParams = thunkAPI.getState().bqProgramDashboardSlice.dashboardParams;
      const body: BQDashboardBody = {
        start_date: dashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: dashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: dashboardParams.selected_org_code === 'allOrgs' ? null : dashboardParams.selected_org_code,
        app_code: AppCode.BuddysQuest,
        for_leps: true,
        for_service_providers: false,
        unlock_codes: dashboardParams.unlock_codes,
      };
      promises.push(thunkAPI.dispatch(getMainDashboard(body)));
      promises.push(thunkAPI.dispatch(getChatActivity(body)));
      promises.push(thunkAPI.dispatch(getFeelings(body)));
      promises.push(thunkAPI.dispatch(getGuestEngagement(body)));
      promises.push(thunkAPI.dispatch(getRiskFactors(body)));
    } catch (e) {
      logError(e, ['bqProgramDashboardSlice', 'loadDashboard']);
    }
  },
);

export const getMainDashboard = createAsyncThunk<DashboardData, BQDashboardBody, AsyncThunkConfig>(
  'bqProgramDashboard/getMainDashboard',
  async (body, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel();
      });
      const response = (await axios.post('v2_program_dashboard', body, {
        cancelToken: source.token,
      })) as DashboardData;
      const bqOrgs = thunkAPI.getState().orgsSlice.orgsForAdminDic.bq;
      thunkAPI.dispatch(setBQFiltersInfo(getBQFiltersInfo(body, bqOrgs)));
      return response ?? initialState.dashboardData;
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      if (error.name !== 'CanceledError') {
        logError(error, ['bqProgramDashboardSlice', 'getMainDashboard']);
      }
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const getChatActivity = createAsyncThunk<LineChartData[], BQDashboardBody, AsyncThunkConfig>(
  'bqProgramDashboard/getChatActivity',
  async (body, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel();
      });
      const response = (await axios.post('v2_program_dashboard_chat_activity', body, {
        cancelToken: source.token,
      })) as LineChartData[];
      return response ?? initialState.chatActivity;
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      if (error.name !== 'CanceledError') {
        logError(error, ['bqProgramDashboardSlice', 'getChatActivity']);
      }
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const getFeelings = createAsyncThunk<SubjectTotal[], BQDashboardBody, AsyncThunkConfig>(
  'bqProgramDashboard/getFeelings',
  async (body, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel();
      });
      const response = (await axios.post('v0_get_feelings', body, {
        cancelToken: source.token,
      })) as SubjectTotal[];
      return response ?? [];
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      if (error.name !== 'CanceledError') {
        logError(error, ['bqProgramDashboardSlice', 'getFeelings']);
      }
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const getRiskFactors = createAsyncThunk<DomainScore[], BQDashboardBody, AsyncThunkConfig>(
  'bqProgramDashboard/getRiskFactors',
  async (body, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel();
      });
      const response = (await axios.post('v0_get_risk_factors', body, {
        cancelToken: source.token,
      })) as DomainScore[];
      return response ?? [];
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      if (error.name !== 'CanceledError') {
        logError(error, ['bqProgramDashboardSlice', 'getRiskFactors']);
      }
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const getGuestEngagement = createAsyncThunk<EngagementData, BQDashboardBody, AsyncThunkConfig>(
  'bqProgramDashboard/getGuestEngagement',
  async (body, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel();
      });
      const response = (await axios.post('v0_get_guest_engagement', body, {
        cancelToken: source.token,
      })) as EngagementData;
      return response ?? initialState.guest_engagement;
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      if (error.name !== 'CanceledError') {
        logError(error, ['bqProgramDashboardSlice', 'getGuestEngagement']);
      }
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const exportTraceAndAsk = createAsyncThunk<string, undefined, AsyncThunkConfig>(
  'bqProgramDashboard/exportTraceAndAsk',
  async (_, thunkAPI) => {
    try {
      const dashboardParams = thunkAPI.getState().bqProgramDashboardSlice.dashboardParams;
      const body: BQDashboardBody = {
        start_date: dashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: dashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: dashboardParams.selected_org_code === 'allOrgs' ? null : dashboardParams.selected_org_code,
        app_code: AppCode.BuddysQuest,
        for_leps: true,
        for_service_providers: false,
        unlock_codes: dashboardParams.unlock_codes,
      };
      const response = (await axios.post('v2_trace_and_ask_export', body)) as string;
      return response;
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      logError(error, ['bqProgramDashboardSlice', 'exportTraceAndAsk']);
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const exportFeelings = createAsyncThunk<string, undefined, AsyncThunkConfig>(
  'bqProgramDashboard/exportFeelings',
  async (_, thunkAPI) => {
    try {
      const dashboardParams = thunkAPI.getState().bqProgramDashboardSlice.dashboardParams;
      const body: BQDashboardBody = {
        start_date: dashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: dashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: dashboardParams.selected_org_code === 'allOrgs' ? null : dashboardParams.selected_org_code,
        app_code: AppCode.BuddysQuest,
        for_leps: true,
        for_service_providers: false,
        unlock_codes: dashboardParams.unlock_codes,
      };
      const response = (await axios.post('v0_export_bq_feelings', body)) as string;
      return response;
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      logError(error, ['bqProgramDashboardSlice', 'exportFeelings']);
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const exportFeedback = createAsyncThunk<string, undefined, AsyncThunkConfig>(
  'bqProgramDashboard/exportFeedback',
  async (_, thunkAPI) => {
    try {
      const dashboardParams = thunkAPI.getState().bqProgramDashboardSlice.dashboardParams;
      const body: BQDashboardBody = {
        start_date: dashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: dashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: dashboardParams.selected_org_code === 'allOrgs' ? null : dashboardParams.selected_org_code,
        app_code: AppCode.BuddysQuest,
        for_leps: true,
        for_service_providers: false,
        unlock_codes: dashboardParams.unlock_codes,
      };
      const response = (await axios.post('v2_feedback_export', body)) as string;
      return response;
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      logError(error, ['bqProgramDashboardSlice', 'exportFeedback']);
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const exportPeerHours = createAsyncThunk<string, undefined, AsyncThunkConfig>(
  'bqProgramDashboard/exportPeerHours',
  async (_, thunkAPI) => {
    try {
      const dashboardParams = thunkAPI.getState().bqProgramDashboardSlice.dashboardParams;
      const body: BQDashboardBody = {
        start_date: dashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: dashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: dashboardParams.selected_org_code === 'allOrgs' ? null : dashboardParams.selected_org_code,
        app_code: AppCode.BuddysQuest,
        for_leps: true,
        for_service_providers: false,
        unlock_codes: dashboardParams.unlock_codes,
      };
      const response = (await axios.post('v2_advocate_hours_export', body)) as string;
      return response;
    } catch (e) {
      const error = e as AxiosError<ErrorMessage>;
      logError(error, ['bqProgramDashboardSlice', 'exportPeerHours']);
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const fetchAllUnlockCodes = createAsyncThunk<UnlockCodeDetails[], undefined, AsyncThunkConfig>(
  'bqProgramDashboard/fetchAllUnlockCodes',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.post('v0_unlock_codes')) as UnlockCodeDetails[];
      return response ?? [];
    } catch (e) {
      logError(e, ['bqProgramDashboardSlice', 'fetchAllUnlockCodes']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const bqProgramDashboardSlice = createSlice({
  name: 'bqProgramDashboard',
  initialState,
  reducers: {
    handleParamsChange: (state, action: PayloadAction<Partial<BQDashboardParams>>) => {
      state.dashboardParams = { ...state.dashboardParams, ...action.payload };
    },
    showDashboardModal: (state, action: PayloadAction<DASHBOARD_MODAL>) => {
      state.modal = action.payload;
    },
    closeDashboardModal: (state) => {
      state.modal = DASHBOARD_MODAL.IDLE;
    },
    handleModalTableChange: (state, action: PayloadAction<Partial<TableChange<ModalTypes>>>) => {
      const pagination = action.payload.pagination;
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<ModalTypes>;
      state.requestModalParam.pagination = pagination || initialState.requestModalParam.pagination;
      state.requestModalParam.sortedInfo = sorter || initialState.requestModalParam.sortedInfo;
      state.requestModalParam.filteredInfo = filters || initialState.requestModalParam.filteredInfo;
    },
    clearModalFilter: (state) => {
      state.requestModalParam.pagination = initialState.requestModalParam.pagination;
      state.requestModalParam.sortedInfo = initialState.requestModalParam.sortedInfo;
      state.requestModalParam.filteredInfo = initialState.requestModalParam.filteredInfo;
    },
    setBQFiltersInfo: (state, action: PayloadAction<string>) => {
      state.filtersInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllUnlockCodes.fulfilled, (state, action) => {
        state.allUnlockCodes = action.payload;
      })
      .addCase(getMainDashboard.pending, (state, _action) => {
        state.filtersInfo = '';
        state.dashboardApiStatus = APIStatus.PENDING;
      })
      .addCase(getMainDashboard.fulfilled, (state, action) => {
        state.dashboardData = action.payload;
        state.dashboardApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getMainDashboard.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.dashboardApiStatus = APIStatus.ERROR;
        }
      })
      .addCase(getFeelings.pending, (state, _action) => {
        state.feelingsApiStatus = APIStatus.PENDING;
      })
      .addCase(getFeelings.fulfilled, (state, action) => {
        state.feelings = action.payload;
        state.feelingsApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getFeelings.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.feelingsApiStatus = APIStatus.ERROR;
        }
      })
      .addCase(exportFeelings.fulfilled, (state, action) => {
        state.csv_exports.feelings_csv = action.payload;
      })
      .addCase(exportFeedback.fulfilled, (state, action) => {
        state.csv_exports.feedback_csv = action.payload;
      })
      .addCase(exportPeerHours.fulfilled, (state, action) => {
        state.csv_exports.peers_hours_csv = action.payload;
      })
      .addCase(exportTraceAndAsk.pending, (state, _action) => {
        state.traceAndAskApiStatus = APIStatus.PENDING;
      })
      .addCase(exportTraceAndAsk.fulfilled, (state, action) => {
        state.csv_exports.trace_and_ask_csv = action.payload;
        state.traceAndAskApiStatus = APIStatus.FULFILLED;
      })
      .addCase(exportTraceAndAsk.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.traceAndAskApiStatus = APIStatus.ERROR;
        }
      })
      .addCase(getGuestEngagement.pending, (state, _action) => {
        state.engagementApiStatus = APIStatus.PENDING;
      })
      .addCase(getGuestEngagement.fulfilled, (state, action) => {
        state.guest_engagement = action.payload;
        state.engagementApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getGuestEngagement.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.engagementApiStatus = APIStatus.ERROR;
        }
      })
      .addCase(getRiskFactors.pending, (state, _action) => {
        state.riskSignalsApiStatus = APIStatus.PENDING;
      })
      .addCase(getRiskFactors.fulfilled, (state, action) => {
        state.risk_signals = action.payload;
        state.riskSignalsApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getRiskFactors.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.riskSignalsApiStatus = APIStatus.ERROR;
        }
      })
      .addCase(getChatActivity.pending, (state, _action) => {
        state.chatActivityApiStatus = APIStatus.PENDING;
      })
      .addCase(getChatActivity.fulfilled, (state, action) => {
        // Remove the search statistics, since we don't have them for the BQ app
        state.chatActivity.data = action.payload.filter((d) => d.name === null || d.name === 'Total chats (per day)');
        state.chatActivityApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getChatActivity.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.dashboardApiStatus = APIStatus.ERROR;
        }
      });
  },
});

export const {
  handleParamsChange,
  showDashboardModal,
  closeDashboardModal,
  handleModalTableChange,
  clearModalFilter,
  setBQFiltersInfo,
} = bqProgramDashboardSlice.actions;
